import React from 'react';
import { AlertCircle, Loader2, Paperclip, Upload } from 'lucide-react';
import { Badge, Button, Input } from '@roger/ui-components';
import type {
  TaskListResponse,
  WoundAssessmentResponse,
  WoundAttachmentResponse,
} from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import { useClosedEpisodeLock } from '../../utils/closedEpisodeLock';
import WoundAttachmentRow from './WoundAttachmentRow';

const formatAssessmentDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatSize = (a: WoundAssessmentResponse): string => {
  if (a.size_na) return 'N/A';
  const parts = [a.length_cm, a.width_cm, a.depth_cm].map(n =>
    n != null ? `${n}` : '–'
  );
  if (parts.every(p => p === '–')) return 'Not measured';
  return `${parts[0]} × ${parts[1]} × ${parts[2]} cm`;
};

const formatVisitType = (visitType: string): string =>
  visitType
    .replace(/_/g, ' ')
    .replace(/\b(\w)(\w*)/g, (_match, first, rest) => {
      const word = first + rest;
      if (/^[A-Z][A-Z0-9]*$/.test(word) && word.length <= 5) return word;
      return first.toUpperCase() + rest.toLowerCase();
    });

interface WoundAssessmentCardProps {
  assessment: WoundAssessmentResponse;
  attachments: WoundAttachmentResponse[];
  attachmentPreviewUrls: Record<string, string>;
  attachmentPreviewFailedIds?: string[];
  readOnly: boolean;
  visits: TaskListResponse[];
  isUploadingAttachment: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onUploadAttachment: (file: File) => void;
  onDownloadAttachment: (attachment: WoundAttachmentResponse) => void;
  onDeleteAttachment: (attachmentId: string) => Promise<void>;
  onPreviewAttachment: (
    attachment: WoundAttachmentResponse,
    url: string
  ) => void;
}

const WoundAssessmentCard: React.FC<WoundAssessmentCardProps> = ({
  assessment,
  attachments,
  attachmentPreviewUrls,
  attachmentPreviewFailedIds = [],
  readOnly,
  visits,
  isUploadingAttachment,
  onEdit,
  onDelete,
  onUploadAttachment,
  onDownloadAttachment,
  onDeleteAttachment,
  onPreviewAttachment,
}) => {
  const inputId = `upload-attachment-${assessment.id}`;

  const ownVisit = visits.find(v => v.id === assessment.visit_id);
  const visitLabel = ownVisit
    ? `${ownVisit.display_name ?? formatVisitType(ownVisit.task_type)} — ${formatDateToMMDDYYYY(ownVisit.task_date ?? ownVisit.scheduled_date)}`
    : formatAssessmentDate(assessment.created_at);
  // An assessment belongs to the visit it was recorded on, and the backend
  // gates edit and delete on that visit alone. The drawer's `readOnly`
  // describes the visit the drawer was opened from, which is a different
  // visit for every earlier assessment, so Edit and Delete key on the
  // assessment's own visit and ignore `readOnly`. A visit the list cannot
  // name is treated as locked. Attachments stay on `readOnly`: the backend
  // leaves them ungated, so the drawer's context is the only rule they have.
  // A closed period locks its assessments too, for anyone without the
  // amend-closed key (EMR-1859).
  const { locked: closedEpisodeLocked } = useClosedEpisodeLock(
    ownVisit?.episode_closed
  );
  const canEditAssessment =
    !!ownVisit &&
    EDITABLE_STATUSES.has(ownVisit.status) &&
    !closedEpisodeLocked;

  return (
    <div className='border rounded-md p-3 space-y-2 bg-card'>
      <div className='flex items-start justify-between gap-2'>
        <div className='space-y-1 flex-1 min-w-0'>
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='text-sm font-medium'>{visitLabel}</span>
            {!assessment.is_addressed && (
              <Badge variant='destructive' className='text-xs'>
                <AlertCircle className='h-3 w-3 mr-1' />
                Not addressed
              </Badge>
            )}
          </div>
          <p className='text-xs text-muted-foreground'>
            Size: {formatSize(assessment)}
          </p>
          <div className='flex gap-2 flex-wrap'>
            {assessment.drainage && assessment.drainage !== 'not_assessed' && (
              <Badge variant='outline' className='text-xs'>
                Drainage: {assessment.drainage}
              </Badge>
            )}
            {assessment.odor && assessment.odor !== 'not_assessed' && (
              <Badge variant='outline' className='text-xs'>
                Odor: {assessment.odor}
              </Badge>
            )}
          </div>
        </div>
        {canEditAssessment && (
          <div className='flex gap-1 shrink-0'>
            <Button variant='outline' size='sm' onClick={onEdit}>
              Edit
            </Button>
            <Button variant='outline' size='sm' onClick={onDelete}>
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Per-assessment attachments */}
      <div className='pt-1 border-t space-y-1.5'>
        <div className='flex items-center justify-between'>
          <span className='text-[11px] font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1'>
            <Paperclip className='h-3 w-3' />
            Attachments
            {attachments.length > 0 && (
              <span className='ml-0.5'>({attachments.length})</span>
            )}
          </span>
          {!readOnly && (
            <>
              <Input
                id={inputId}
                type='file'
                className='hidden'
                accept='.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif'
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    await onUploadAttachment(file);
                    e.target.value = '';
                  }
                }}
              />
              <label htmlFor={inputId}>
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-6 px-2 text-xs cursor-pointer'
                  disabled={isUploadingAttachment}
                  asChild
                >
                  <span>
                    {isUploadingAttachment ? (
                      <Loader2 className='h-3 w-3 animate-spin' />
                    ) : (
                      <Upload className='h-3 w-3' />
                    )}
                  </span>
                </Button>
              </label>
            </>
          )}
        </div>
        {attachments.length > 0 ? (
          <div className='space-y-1'>
            {attachments.map(att => (
              <WoundAttachmentRow
                key={att.id}
                attachment={att}
                previewUrl={attachmentPreviewUrls[att.id]}
                previewUnavailable={attachmentPreviewFailedIds.includes(att.id)}
                onDownload={onDownloadAttachment}
                onDelete={readOnly ? undefined : onDeleteAttachment}
                onPreview={onPreviewAttachment}
              />
            ))}
          </div>
        ) : (
          <p className='text-[11px] text-muted-foreground italic'>
            No attachments
          </p>
        )}
      </div>
    </div>
  );
};

export default WoundAssessmentCard;
